import { useEffect, useRef } from 'react'
import { type UseFormRegister, type UseFormSetValue, type UseFormTrigger, useForm } from 'react-hook-form'
import { Checkbox } from '@/common/ui/checkbox'
import { H2, Label, LabelInput } from '@/common/ui'
import { LabelDatePicker } from '@/common/ui/LabelDatePicker'
import type { AddressDetails, AddressFormSchema } from '@/common/types'
import { APPLICATION_COPY, ONBOARDING_COPY } from '@/locales/en'
import type { AddAccountParticipantDraft } from '../types'
import { SearchableAddressForm } from '@/user/address/SearchableAddressForm'
import { MobileNumberField } from '@/onboarding/shared/components/MobileNumberField'
import { extractCountryCodeAndMobile } from '@/user/mobile/MobileUtils'

type Props = {
    participant: AddAccountParticipantDraft
    onParticipantChange: (updates: Partial<AddAccountParticipantDraft>) => void
}

type ParticipantDetailsFormValues = AddressFormSchema & {
    salutation: string
    firstName: string
    middleName: string
    lastName: string
    email: string
    dateOfBirth: string
    countryCode: string
    mobile: string
}

function pad2(value: number) {
    return String(value).padStart(2, '0')
}

function formatDateAsLocalIso(date: Date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function parseLocalIsoDate(value?: string) {
    if (!value) return undefined

    const [year, month, day] = value.split('-').map(Number)
    if (!year || !month || !day) return undefined

    return new Date(year, month - 1, day)
}

function normalizeFormAddress(address?: Partial<AddressDetails>): AddressFormSchema['postal'] {
    return {
        ...address,
        streetNumber: address?.streetNumber ?? '',
        streetName: address?.streetName ?? '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        postCode: address?.postCode ?? '',
        country: address?.country ?? '',
        addressPrefix: address?.addressPrefix ?? '',
        streetType: address?.streetType ?? '',
    }
}

function hasAddressValue(address?: Partial<AddressDetails>) {
    if (!address) return false

    return Boolean(
        address.addressId ||
            address.addressPrefix ||
            address.streetNumber ||
            address.streetName ||
            address.streetType ||
            address.city ||
            address.state ||
            address.postCode ||
            address.country,
    )
}

function normalizeDraftAddress(address?: Partial<AddressDetails>): AddressDetails | undefined {
    const normalized = normalizeFormAddress(address) as AddressDetails
    return hasAddressValue(normalized) ? normalized : undefined
}

export function AddAccountParticipantDetailsStep({ participant, onParticipantChange }: Props) {
    const onParticipantChangeRef = useRef(onParticipantChange)
    const { countryCode: defaultCountryCode, mobile: defaultMobile } = extractCountryCodeAndMobile(participant.mobile)
    const { control, getValues, register, setValue, trigger, watch } = useForm<ParticipantDetailsFormValues>({
        defaultValues: {
            residential: normalizeFormAddress(participant.residential) as AddressDetails,
            differentPostalAddress: participant.differentPostalAddress,
            postal: normalizeFormAddress(
                participant.differentPostalAddress ? participant.postal : participant.residential,
            ),
            salutation: participant.salutation,
            firstName: participant.firstName,
            middleName: participant.middleName,
            lastName: participant.lastName,
            email: participant.email,
            dateOfBirth: participant.dateOfBirth,
            countryCode: defaultCountryCode,
            mobile: defaultMobile,
        },
    })

    onParticipantChangeRef.current = onParticipantChange

    const residential = watch('residential')
    const postal = watch('postal')
    const differentPostalAddress = watch('differentPostalAddress', participant.differentPostalAddress)
    const salutation = watch('salutation')
    const firstName = watch('firstName')
    const middleName = watch('middleName')
    const lastName = watch('lastName')
    const email = watch('email')
    const dateOfBirth = watch('dateOfBirth')
    const countryCode = watch('countryCode')
    const mobile = watch('mobile')
    const residentialSyncKey = JSON.stringify(residential)
    const postalSyncKey = JSON.stringify(postal)
    const residentialDefaultValue = participant.residential
    const postalDefaultValue = participant.postal
    const addressRegister = register as unknown as UseFormRegister<AddressFormSchema>
    const addressSetValue = setValue as unknown as UseFormSetValue<AddressFormSchema>
    const addressTrigger = trigger as unknown as UseFormTrigger<AddressFormSchema>

    useEffect(() => {
        if (differentPostalAddress) return

        setValue('postal', normalizeFormAddress(residential))
    }, [differentPostalAddress, residential, setValue])

    useEffect(() => {
        const normalizedResidential = normalizeDraftAddress(residential)
        const normalizedPostal = normalizeDraftAddress(postal)
        onParticipantChangeRef.current({
            salutation,
            firstName,
            middleName,
            lastName,
            email,
            mobile: mobile ? `${countryCode}${mobile}` : '',
            dateOfBirth,
            differentPostalAddress: Boolean(differentPostalAddress),
            postal: normalizedPostal,
            residential: normalizedResidential,
        })
    }, [
        salutation,
        countryCode,
        dateOfBirth,
        differentPostalAddress,
        email,
        firstName,
        lastName,
        middleName,
        mobile,
        postal,
        postalSyncKey,
        residential,
        residentialSyncKey,
    ])

    return (
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-12">
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                    <H2 className="self-stretch justify-start text-brand-forest text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                        {APPLICATION_COPY.participantDetails.title}
                    </H2>
                    <div className="self-stretch justify-start text-black text-base font-normal font-['Inter'] leading-6">
                        {APPLICATION_COPY.participantDetails.description}
                    </div>
                </div>
            </div>
            <div className="self-stretch flex flex-col justify-center items-start gap-6">
                <div className="self-stretch grid grid-cols-1 md:grid-cols-2 gap-6">
                    <LabelInput
                        {...register('salutation')}
                        id="participant-salutation"
                        label={APPLICATION_COPY.participantDetails.titleLabel || 'Title'}
                        placeholder={APPLICATION_COPY.participantDetails.titlePlaceholder || ''}
                    />
                    <LabelInput
                        {...register('firstName')}
                        id="participant-first-name"
                        label={APPLICATION_COPY.participantDetails.firstNameLabel}
                        placeholder={APPLICATION_COPY.participantDetails.firstNamePlaceholder}
                    />
                </div>

                <div className="self-stretch grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="w-full flex flex-col gap-1.5">
                        <div className="inline-flex justify-start items-start gap-1.5">
                            <span className="justify-start text-slate-900 text-sm font-medium font-['Inter'] leading-5">
                                {APPLICATION_COPY.participantDetails.middleNameLabel}
                            </span>
                            <span className="justify-start text-slate-500 text-sm font-medium font-['Inter'] leading-5">
                                {APPLICATION_COPY.participantDetails.middleNameOptionalLabel}
                            </span>
                        </div>
                        <LabelInput
                            {...register('middleName')}
                            id="participant-middle-name"
                            placeholder={APPLICATION_COPY.participantDetails.middleNamePlaceholder}
                        />
                    </div>
                    <LabelInput
                        {...register('lastName')}
                        id="participant-last-name"
                        label={APPLICATION_COPY.participantDetails.lastNameLabel}
                        placeholder={APPLICATION_COPY.participantDetails.lastNamePlaceholder}
                    />
                </div>

                <div className="self-stretch grid grid-cols-1 md:grid-cols-2 gap-6">
                    <LabelDatePicker
                        id="participant-date-of-birth"
                        label={ONBOARDING_COPY.aboutYou.form.dateOfBirth}
                        placeholder={ONBOARDING_COPY.aboutYou.form.dateOfBirthPlaceholder}
                        value={parseLocalIsoDate(dateOfBirth)}
                        onChange={(date) => setValue('dateOfBirth', date ? formatDateAsLocalIso(date) : '')}
                        minDate={new Date('1900-01-01')}
                        maxDate={new Date()}
                    />
                    <MobileNumberField
                        control={control}
                        register={register}
                        setValue={setValue}
                        trigger={trigger}
                        countryCode={countryCode}
                        mobile={mobile}
                    />
                </div>

                <div className="self-stretch grid grid-cols-1 gap-6">
                    <LabelInput
                        {...register('email')}
                        id="participant-email"
                        type="email"
                        label={APPLICATION_COPY.participantDetails.emailLabel}
                        placeholder={APPLICATION_COPY.participantDetails.emailPlaceholder}
                    />
                </div>
                <div className="self-stretch flex flex-col justify-center items-start gap-6">
                    <div className="self-stretch flex flex-col justify-start items-start">
                        <Label>{ONBOARDING_COPY.aboutYou.form.residentialAddress}</Label>
                        <div className="w-full">
                            <SearchableAddressForm
                                type="residential"
                                defaultValues={residentialDefaultValue}
                                register={addressRegister}
                                setValue={addressSetValue}
                                trigger={addressTrigger}
                            />
                        </div>
                        <div className="inline-flex justify-start items-center gap-2">
                            <Checkbox
                                id="participant-different-postal-address"
                                checked={Boolean(differentPostalAddress)}
                                onCheckedChange={(checked) => {
                                    const nextValue = checked === true
                                    setValue('differentPostalAddress', nextValue)

                                    if (!nextValue) {
                                        setValue('postal', normalizeFormAddress(getValues('residential')))
                                    }
                                }}
                            />
                            <Label
                                htmlFor="participant-different-postal-address"
                                className="ml-1 mb-0 text-xs leading-5 text-black"
                            >
                                {ONBOARDING_COPY.aboutYou.form.differentPostalAddress}
                            </Label>
                        </div>
                    </div>

                    {differentPostalAddress && (
                        <div className="self-stretch flex flex-col justify-start items-start gap-2">
                            <Label>{ONBOARDING_COPY.aboutYou.form.postalAddress}</Label>
                            <div className="w-full">
                                <SearchableAddressForm
                                    type="postal"
                                    defaultValues={postalDefaultValue}
                                    register={addressRegister}
                                    setValue={addressSetValue}
                                    trigger={addressTrigger}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
